// TradesView.jsx
// Trade review screen
// Top: filters + trade detail + navigation
// Bottom: full ChartsView (same as Charts screen)
// When a trade is selected, all charts jump to entry date

import React, { useState, useEffect, useCallback, useRef } from 'react';
import useStore from '../store';
import ChartsView from './ChartsView';
import {
  fetchTrades,
  fetchTradeFilters,
  updateTradeReview,
  uploadTradebook,
  createTempWatchlist,
} from '../api';

// ============================================================
// CONSTANTS
// ============================================================
const MONTHS = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' }, { value: 4, label: 'Apr' },
  { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' }, { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
];

// ============================================================
// TRADE DETAIL PANEL
// Shows entry/exit info for current trade
// ============================================================
function TradeDetail({ trade, onRemarksChange, onRatingChange }) {
  if (!trade) return (
    <div style={detail.empty}>No trade selected</div>
  );

  const pnlColor = trade.net_pnl >= 0 ? '#26a69a' : '#ef5350';

  return (
    <div style={detail.wrapper}>

      {/* Main detail row */}
      <div style={detail.row}>

        <div style={detail.cell}>
          <div style={detail.cellLabel}>Entry</div>
          <div style={detail.cellValue}>
            {trade.entry_date} {trade.entry_time?.slice(0,5)}
          </div>
          <div style={{...detail.cellValue, color: trade.entry_action?.toLowerCase() === 'buy' ? '#26a69a' : '#ef5350'}}>
            {trade.entry_action?.toUpperCase()} @ {trade.entry_price} × {trade.qty}
          </div>
        </div>

        <div style={detail.cell}>
          <div style={detail.cellLabel}>Exit</div>
          <div style={detail.cellValue}>
            {trade.exit_date} {trade.exit_time?.slice(0,5)}
          </div>
          <div style={{...detail.cellValue, color: trade.exit_action?.toLowerCase() === 'sell' ? '#ef5350' : '#26a69a'}}>
            {trade.exit_action?.toUpperCase()} @ {trade.exit_price} × {trade.exit_qty}
          </div>
        </div>

        <div style={detail.cell}>
          <div style={detail.cellLabel}>P&L</div>
          <div style={{...detail.cellValue, color: pnlColor, fontWeight: 'bold', fontSize: '14px'}}>
            {trade.net_pnl >= 0 ? '+' : ''}{trade.net_pnl?.toFixed(2)}
          </div>
          <div style={{...detail.cellValue, color: '#999'}}>
            Gross: {trade.gross_pnl?.toFixed(2)}
          </div>
        </div>

        <div style={detail.cell}>
          <div style={detail.cellLabel}>Rating</div>
          <div style={detail.ratingRow}>
            {[1,2,3,4,5].map((r) => (
              <button
                key={r}
                onClick={() => onRatingChange(r)}
                style={{
                  ...detail.ratingStar,
                  color: (trade.rating || 0) >= r ? '#f59e0b' : '#ddd',
                }}
              >★</button>
            ))}
          </div>
        </div>

        <div style={detail.cell}>
          <div style={detail.cellLabel}>Status</div>
          <div style={{
            ...detail.badge,
            backgroundColor: trade.reviewed_at ? '#e8f5e9' : '#fff8e1',
            color:           trade.reviewed_at ? '#2e7d32' : '#f59e0b',
          }}>
            {trade.reviewed_at ? '✓ Reviewed' : '○ Not reviewed'}
          </div>
        </div>

        {/* Remarks — fills remaining space */}
        <div style={{...detail.cell, flex: 1, minWidth: '200px'}}>
          <div style={detail.cellLabel}>Remarks</div>
          <textarea
            style={detail.remarks}
            placeholder="Add remarks..."
            value={trade.remarks || ''}
            onChange={(e) => onRemarksChange(e.target.value)}
          />
        </div>

      </div>
    </div>
  );
}

// ============================================================
// MAIN TRADES VIEW
// ============================================================
function TradesView() {
  const { setSelectedSymbol, setJumpTrigger,
          setMarkersConfig } = useStore();

  // Filter state
  const [filterOptions, setFilterOptions] = useState({ years: [], instruments: [], symbols: [] });
  const [filters, setFilters] = useState({
    year: '', month: '', instrument: '', symbol: '', pnl_filter: '', reviewed: '',
  });

  // Trade list state
  const [trades,     setTrades]     = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading,    setLoading]    = useState(false);

  // ── Loaded selection (set only when "Load on Chart" is pressed) ──
  // tradesBySymbol: { SYMBOL: [trade, ...] } — the scoped marker/nav source
  // loadedSymbols : ordered unique symbols in the loaded selection
  // activeSymbol  : which symbol's trades prev/next currently walks
  const [tradesBySymbol, setTradesBySymbol] = useState({});
  const [loadedSymbols,  setLoadedSymbols]  = useState([]);
  const [activeSymbol,   setActiveSymbol]   = useState(null);
  const [loadMsg,        setLoadMsg]        = useState(null);

  // Remarks save debounce
  const [remarksTimer, setRemarksTimer] = useState(null);
  // Detail panel collapsed state
  const [detailCollapsed, setDetailCollapsed] = useState(false);

  // Tradebook upload
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                 // allow re-selecting same file
    if (!file) return;
    setUploadMsg({ type: 'info', text: 'Uploading…', details: [] });
    try {
      const res   = await uploadTradebook(file);
      const dupes = res.skipped_duplicates?.length || 0;
      setUploadMsg({
        type:    'ok',
        text:    `Imported ${res.inserted} trade(s)` + (dupes ? ` · ${dupes} duplicate(s) skipped` : ''),
        details: res.skipped_duplicates || [],
      });
      fetchTradeFilters().then(setFilterOptions).catch(() => {});
      loadTrades();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setUploadMsg({ type: 'err', text: detail.message || 'Upload failed', details: detail.errors || [] });
      } else {
        setUploadMsg({ type: 'err', text: detail || 'Upload failed', details: [] });
      }
    }
  };

  // ── Load filter options on mount
  useEffect(() => {
    fetchTradeFilters()
      .then(setFilterOptions)
      .catch(console.error);
  }, []);

  // ── Load trades when filters change
  useEffect(() => {
    loadTrades();
  }, [filters]);

  
  const setTradeReviewMode = useStore((s) => s.setTradeReviewMode);

  useEffect(() => {
    setTradeReviewMode(true);
    return () => {
      setTradeReviewMode(false);
      // Clear trade markers so they never bleed into Charts/EMA report
      setMarkersConfig({ enabled: false, fetchFn: null, filters: {} });
    };
  }, []);
  
  // ── Active symbol's trades (scoped navigation source) ──
  // Navigation walks ONLY the active symbol's trades from the loaded
  // selection. currentIdx now indexes into THIS list, not the flat one.
  const activeTrades = activeSymbol ? (tradesBySymbol[activeSymbol] || []) : [];
  const currentTrade = activeTrades[currentIdx] || null;

  // ── Multi-day + exit-reachability helpers ──
  // A trade is "positional" if entry and exit fall on different dates.
  const isMultiDay = (t) =>
    t && t.exit_date &&
    String(t.entry_date).slice(0, 10) !== String(t.exit_date).slice(0, 10);

  // Loaded date span for the active symbol — earliest to latest date
  // present in the loaded selection (entries AND exits). Go-to-Exit is
  // reachable only if the exit falls inside this span (markers=selection).
  const loadedSpan = (() => {
    if (!activeTrades.length) return null;
    let min = Infinity, max = -Infinity;
    activeTrades.forEach((t) => {
      const e = new Date(`${String(t.entry_date).slice(0,10)}T00:00:00`).getTime();
      if (e < min) min = e;
      if (e > max) max = e;
      if (t.exit_date) {
        const x = new Date(`${String(t.exit_date).slice(0,10)}T00:00:00`).getTime();
        if (x < min) min = x;
        if (x > max) max = x;
      }
    });
    return { min, max };
  })();

  const exitReachable = (t) => {
    if (!t || !t.exit_date || !loadedSpan) return false;
    const x = new Date(`${String(t.exit_date).slice(0,10)}T00:00:00`).getTime();
    return x >= loadedSpan.min && x <= loadedSpan.max;
  };

  // ── Jump helpers (Behavior B — reuse the reliable jumpToDate path) ──
  const goToEntry = () => {
    if (!currentTrade) return;
    setSelectedSymbol(activeSymbol);
    setJumpTrigger(String(currentTrade.entry_date).slice(0, 10));
  };
  const goToExit = () => {
    if (!currentTrade || !exitReachable(currentTrade)) return;
    setSelectedSymbol(activeSymbol);
    setJumpTrigger(String(currentTrade.exit_date).slice(0, 10));
  };

  // ── When the active trade changes, drive the chart (Behavior B) ──
  // Only runs AFTER Load-on-Chart has populated the selection. Applying
  // filters alone does nothing to the chart.
  useEffect(() => {
    if (!activeSymbol) return;
    const trade = activeTrades[currentIdx];
    if (!trade) return;
    setSelectedSymbol(activeSymbol);
    setJumpTrigger(String(trade.entry_date).slice(0, 10));
  }, [activeSymbol, currentIdx]);

  // ── Symbol pick from the chip strip ──
  const handleSymbolPick = (sym) => {
    if (!tradesBySymbol[sym]) return;
    setActiveSymbol(sym);
    setCurrentIdx(0);   // start at first trade of the picked symbol
  };

  // ── Load trades
  const loadTrades = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.year)        params.year        = filters.year;
      if (filters.month)       params.month       = filters.month;
      if (filters.instrument)  params.instrument  = filters.instrument;
      if (filters.symbol)      params.symbol      = filters.symbol;
      if (filters.pnl_filter)  params.pnl_filter  = filters.pnl_filter;
      if (filters.reviewed !== '') {
        params.reviewed = filters.reviewed === 'true';
      }

      const data = await fetchTrades(params);
      setTrades(data || []);
      setCurrentIdx(0);
    } catch (err) {
      console.error('Load trades error:', err);
    }
    setLoading(false);
  };

  // ── Load current filtered trades onto the chart ──────────────
  // Explicit action: builds temp watchlist + groups trades by symbol
  // + pushes marker config. Markers = exactly this selection.
  const handleLoadOnChart = async () => {
    if (!trades || trades.length === 0) {
      setLoadMsg({ type: 'err', text: 'No trades to load' });
      return;
    }
    setLoadMsg({ type: 'info', text: 'Loading…' });
    try {
      // Group by symbol, preserving first-seen order
      const bySymbol = {};
      const order    = [];
      trades.forEach((t) => {
        if (!t.symbol) return;
        if (!bySymbol[t.symbol]) { bySymbol[t.symbol] = []; order.push(t.symbol); }
        bySymbol[t.symbol].push(t);
      });

      if (order.length === 0) {
        setLoadMsg({ type: 'err', text: 'No symbols in selection' });
        return;
      }

      // Temp watchlist from the selected symbols (reuses existing endpoint)
      const res = await createTempWatchlist(order);

      // NOTE: we do NOT touch the global watchlist — the Trades chart reads
      // symbols via the tradesSymbols prop, so nothing leaks into Charts menu.

      // Marker config for ChartNew — markerKind 'trade' selects the trade
      // builder (added in Wave B). markersBySymbol carries the raw trades.
      setMarkersConfig({
        enabled:        true,
        markerKind:     'trade',
        fetchFn:        null,
        filters:        {},
        markersBySymbol: bySymbol,
      });

      // Remember the loaded selection for navigation + chip strip
      setTradesBySymbol(bySymbol);
      setLoadedSymbols(order);

      // Start on the first symbol, first trade
      const firstSym = order[0];
      setActiveSymbol(firstSym);
      setSelectedSymbol(firstSym);
      setCurrentIdx(0);

      setLoadMsg({
        type: 'ok',
        text: `Loaded ${trades.length} trade(s) · ${order.length} symbol(s)`,
      });
    } catch (err) {
      console.error('Load on chart error:', err);
      setLoadMsg({ type: 'err', text: 'Failed to load on chart' });
    }
  };
  
  
  // ── Navigation — scoped to the active symbol's trades only.
  // At the last trade, Next stops (switch symbols via the chip strip).
  const goNext = () => {
    if (currentIdx < activeTrades.length - 1) setCurrentIdx(currentIdx + 1);
  };

  const goPrev = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  // ── Remarks change with auto-save (debounced 1s)
  const handleRemarksChange = (value) => {
    // Update local state immediately
    const updated = [...trades];
    updated[currentIdx] = { ...updated[currentIdx], remarks: value };
    setTrades(updated);

    // Debounce save to DB
    if (remarksTimer) clearTimeout(remarksTimer);
    const timer = setTimeout(async () => {
      try {
        await updateTradeReview(trades[currentIdx].id, value, trades[currentIdx].rating);
      } catch (err) {
        console.error('Save remarks error:', err);
      }
    }, 1000);
    setRemarksTimer(timer);
  };

  // ── Rating change — save immediately
  const handleRatingChange = async (rating) => {
    const updated = [...trades];
    updated[currentIdx] = { ...updated[currentIdx], rating };
    setTrades(updated);
    try {
      await updateTradeReview(trades[currentIdx].id, trades[currentIdx].remarks, rating);
    } catch (err) {
      console.error('Save rating error:', err);
    }
  };

 

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={styles.container}>

      {/* ── FILTERS BAR ── */}
      <div style={styles.filtersBar}>

        <select
          style={styles.select}
          value={filters.year}
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
        >
          <option value="">All Years</option>
          {filterOptions.years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          style={styles.select}
          value={filters.month}
          onChange={(e) => setFilters({ ...filters, month: e.target.value })}
        >
          <option value="">All Months</option>
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>

        <select
          style={styles.select}
          value={filters.instrument}
          onChange={(e) => setFilters({ ...filters, instrument: e.target.value })}
        >
          <option value="">All Instruments</option>
          {filterOptions.instruments.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>

        <select
          style={styles.select}
          value={filters.symbol}
          onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
        >
          <option value="">All Stocks</option>
          {filterOptions.symbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          style={styles.select}
          value={filters.pnl_filter}
          onChange={(e) => setFilters({ ...filters, pnl_filter: e.target.value })}
        >
          <option value="">All Trades</option>
          <option value="profit">Profit Only</option>
          <option value="loss">Loss Only</option>
        </select>

        <select
          style={styles.select}
          value={filters.reviewed}
          onChange={(e) => setFilters({ ...filters, reviewed: e.target.value })}
        >
          <option value="">All</option>
          <option value="false">Unreviewed</option>
          <option value="true">Reviewed</option>
        </select>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,.tsv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button style={styles.uploadBtn} onClick={handleUploadClick}>
          ⬆ Upload Tradebook
        </button>

        <button
          style={styles.loadBtn}
          onClick={handleLoadOnChart}
          disabled={loading || trades.length === 0}
        >
          📈 Load on Chart
        </button>
        {loadMsg && (
          <span style={{
            fontSize: '12px',
            color: loadMsg.type === 'err' ? '#ef5350'
                 : loadMsg.type === 'ok'  ? '#2e7d32' : '#666',
          }}>
            {loadMsg.text}
          </span>
        )}

        {uploadMsg && (
          <span
            style={{
              ...styles.uploadMsg,
              color: uploadMsg.type === 'err' ? '#ef5350'
                   : uploadMsg.type === 'ok'  ? '#2e7d32' : '#666',
            }}
            title={(uploadMsg.details || []).join('\n')}
          >
            {uploadMsg.text}{uploadMsg.details?.length ? '  (hover for details)' : ''}
          </span>
        )}
        
        <span style={styles.tradeCount}>
          {loading ? 'Loading...' : `${trades.length} trade${trades.length !== 1 ? 's' : ''}`}
        </span>

      </div>

      {/* ── TRADE DETAIL PANEL ── */}
      <div style={styles.detailWrapper}>
        {/* Collapse toggle bar — always visible, contains nav */}
        <div style={styles.collapseBar}>
          {/* Prev/Next navigation */}
          <div style={styles.collapseNav}>
            <button
              onClick={goPrev}
              disabled={currentIdx <= 0}
              style={{...styles.navBtnSm, opacity: currentIdx <= 0 ? 0.4 : 1}}
            >← Prev</button>
            <span style={styles.collapseCounter}>
              {activeSymbol && activeTrades.length > 0
                ? `${activeSymbol}  ${currentIdx + 1} / ${activeTrades.length}`
                : '0 / 0'}
            </span>
            <button
              onClick={goNext}
              disabled={currentIdx >= activeTrades.length - 1}
              style={{...styles.navBtnSm, opacity: currentIdx >= activeTrades.length - 1 ? 0.4 : 1}}
            >Next →</button>

            {/* Go-to-Entry / Go-to-Exit — only for multi-day (positional) trades */}
            {isMultiDay(currentTrade) && (
              <>
                <span style={styles.navDivider} />
                <button
                  onClick={goToEntry}
                  style={styles.gotoBtn}
                  title={`Jump to entry (${currentTrade.entry_date})`}
                >⤓ Entry</button>
                <button
                  onClick={goToExit}
                  disabled={!exitReachable(currentTrade)}
                  style={{
                    ...styles.gotoBtn,
                    opacity: exitReachable(currentTrade) ? 1 : 0.4,
                    cursor:  exitReachable(currentTrade) ? 'pointer' : 'not-allowed',
                  }}
                  title={
                    exitReachable(currentTrade)
                      ? `Jump to exit (${currentTrade.exit_date})`
                      : `Exit ${currentTrade.exit_date} is outside the loaded selection`
                  }
                >⤒ Exit</button>
              </>
            )}
          </div>

          {/* Trade summary */}
          <span style={styles.collapseLabel}>
            {currentTrade
              ? `${currentTrade.symbol}  |  ${currentTrade.instrument}  |  ${currentTrade.portal}  |  #${currentTrade.s_no}  |  ${currentTrade.entry_date}  |  ${currentTrade.entry_action?.toUpperCase()}  |  Net P&L: ${currentTrade.net_pnl >= 0 ? '+' : ''}${currentTrade.net_pnl?.toFixed(2)}`
              : 'No trade selected'}
          </span>

          {/* Collapse toggle */}
          <span
            style={styles.collapseIcon}
            onClick={() => setDetailCollapsed(!detailCollapsed)}
            title={detailCollapsed ? 'Expand' : 'Collapse'}
          >
            {detailCollapsed ? '▼' : '▲'}
          </span>
        </div>

        {/* Detail panel — hidden when collapsed */}
        {!detailCollapsed && (
          <TradeDetail
            trade={currentTrade}
            onRemarksChange={handleRemarksChange}
            onRatingChange={handleRatingChange}
          />
        )}
      </div>

      {/* ── CHARTS — trades mode: scoped to loaded selection ── */}
      <div style={styles.chartsContainer}>
        <ChartsView
          tradesMode={true}
          tradesSymbols={loadedSymbols}
          activeSymbol={activeSymbol}
          onSymbolPick={handleSymbolPick}
        />
      </div>

    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    overflow:      'hidden',
    background:    '#f5f5f5',
  },
  filtersBar: {
    display:         'flex',
    alignItems:      'center',
    gap:             '8px',
    padding:         '8px 16px',
    backgroundColor: '#ffffff',
    borderBottom:    '1px solid #e0e0e0',
    flexShrink:      0,
    flexWrap:        'wrap',
  },
  select: {
    padding:      '4px 8px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '12px',
    color:        '#444',
    background:   '#fff',
    cursor:       'pointer',
  },
  tradeCount: {
    marginLeft: 'auto',
    fontSize:   '12px',
    color:      '#888',
  },
  uploadBtn: {
    padding:         '4px 12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
  loadBtn: {
    padding:         '4px 12px',
    backgroundColor: '#00897b',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
  navDivider: {
    display:         'inline-block',
    width:           '1px',
    height:          '18px',
    backgroundColor: '#ddd',
    margin:          '0 4px',
  },
  gotoBtn: {
    padding:         '3px 10px',
    backgroundColor: '#00897b',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    fontSize:        '12px',
    cursor:          'pointer',
  },
  uploadMsg: {
    fontSize:  '12px',
    maxWidth:  '320px',
    overflow:  'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chartsContainer: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
    minWidth: 0,
  },
  detailWrapper: {
    flexShrink:      0,
    backgroundColor: '#ffffff',
    borderBottom:    '1px solid #e0e0e0',
  },
  collapseBar: {
    display:         'flex',
    alignItems:      'center',
    gap:             '12px',
    padding:         '4px 16px',
    backgroundColor: '#f0f4ff',
    borderBottom:    '1px solid #e0e0e0',
    userSelect:      'none',
  },
  collapseLabel: {
    fontSize:   '12px',
    color:      '#444',
    fontWeight: 'bold',
    flex:       1,
  },
  collapseIcon: {
    fontSize: '12px',
    color:    '#2962ff',
    cursor:   'pointer',
    padding:  '0 4px',
  },
  collapseNav: {
    display:    'flex',
    alignItems: 'center',
    gap:        '6px',
    flexShrink: 0,
  },
  collapseCounter: {
    fontSize: '11px',
    color:    '#666',
    minWidth: '40px',
    textAlign:'center',
  },
  navBtnSm: {
    padding:         '3px 8px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontSize:        '11px',
  },
};

// ── TRADE DETAIL STYLES ──
const detail = {
  wrapper: {
    backgroundColor: '#ffffff',
    padding:         '4px 16px 4px 16px',
    flexShrink:      0,
  },
  empty: {
    padding:         '12px 16px',
    backgroundColor: '#ffffff',
    borderBottom:    '1px solid #e0e0e0',
    color:           '#999',
    fontSize:        '13px',
    flexShrink:      0,
  },
  nav: {
    display:        'flex',
    alignItems:     'center',
    gap:            '8px',
    marginBottom:   '6px',
  },
  navBtn: {
    padding:         '4px 12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
  counter: {
    fontSize: '12px',
    color:    '#666',
  },
  info: {
    display:      'flex',
    alignItems:   'center',
    gap:          '8px',
    marginBottom: '6px',
  },
  symbol: {
    fontWeight: 'bold',
    fontSize:   '15px',
    color:      '#131722',
  },
  sno: {
    fontSize: '11px',
    color:    '#999',
  },
  badge: {
    padding:         '2px 8px',
    backgroundColor: '#f0f0f0',
    borderRadius:    '3px',
    fontSize:        '11px',
    color:           '#555',
  },
  row: {
    display:  'flex',
    gap:      '16px',
    flexWrap: 'wrap',
    marginBottom: '6px',
  },
  cell: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '2px',
    minWidth:      '140px',
  },
  cellLabel: {
    fontSize:      '10px',
    fontWeight:    'bold',
    color:         '#999',
    textTransform: 'uppercase',
  },
  cellValue: {
    fontSize: '12px',
    color:    '#333',
  },
  ratingRow: {
    display: 'flex',
    gap:     '2px',
  },
  ratingStar: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '18px',
    padding:    '0',
    lineHeight: '1',
  },
  remarks: {
    width:      '100%',
    height:     '52px',
    padding:    '4px 8px',
    border:     '1px solid #ddd',
    borderRadius:'4px',
    fontSize:   '12px',
    color:      '#333',
    resize:     'vertical',
    boxSizing:  'border-box',
    fontFamily: 'inherit',
  },
};

export default TradesView;